import React, { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "react-query";
import { getOrders } from "../../services/orderService";
import { getGeneralInfo } from "../../services/generalInfoService";
import { toast } from "react-toastify";
import { parseISO } from "date-fns";
import { useNavigate, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ExcelUpload from "../../components/ExcelUpload";
import Pagination from "../../components/Pagination";
import { FiUpload } from "react-icons/fi";
import { formatAmountLegacy } from "../../utils/formatters";

const Orders = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const STORAGE_KEY = "filters:orders";
  let savedFilters = null;
  try {
    savedFilters = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch (_) { }
  const TODAY_KEY = new Date().toLocaleDateString("en-CA");
  if (savedFilters && savedFilters.dateKey !== TODAY_KEY) {
    savedFilters = null;
  }

  // Get navigation state from dashboard
  const dashboardState = location.state;

  const [page, setPage] = useState(location.state?.page || 1);
  const [sortField, setSortField] = useState(
    location.state?.sortField || "refDate"
  );
  const [sortValue, setSortValue] = useState(
    location.state?.sortValue || "DESC"
  );
  const [searchKey, setSearchKey] = useState(location.state?.searchValue || "");
  const [searchValue, setSearchValue] = useState(
    location.state?.searchValue || ""
  );
  const [isSearching, setIsSearching] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExcelUpload, setShowExcelUpload] = useState(false);
  const [postingAccount, setPostingAccount] = useState(
    location.state?.postingAccount || ""
  );
  const [zone, setZone] = useState(location.state?.zone || "");
  const [state, setState] = useState(location.state?.state || "");
  const [status, setStatus] = useState(location.state?.status || "");
  const [fetchType, setFetchType] = useState(
    location.state?.fetchType || "item"
  );
  
  const safeParseDate = (value, fallback) => {
    if (!value) return fallback;
    const d = typeof value === "string" ? parseISO(value) : new Date(value);
    return d && !Number.isNaN(d.getTime()) ? d : fallback;
  };
  const defaultStart = new Date(new Date().setDate(new Date().getDate() - 30));
  const defaultEnd = new Date();
  const [startDate, setStartDate] = useState(() => {
    return (
      safeParseDate(location.state?.startDate, null) ??
      safeParseDate(dashboardState?.startDate, null) ??
      safeParseDate(savedFilters?.startDate, null) ??
      defaultStart
    );
  });
  const [endDate, setEndDate] = useState(() => {
    return (
      safeParseDate(location.state?.endDate, null) ??
      safeParseDate(dashboardState?.endDate, null) ??
      safeParseDate(savedFilters?.endDate, null) ??
      defaultEnd
    );
  });

  // Set status filter from dashboard state or navigation state
  useEffect(() => {
    if (location.state?.status) {
      setStatus(location.state.status);
    } else if (dashboardState?.status) {
      setStatus(dashboardState.status);
    }
  }, [location.state, dashboardState]);

  const limit = 15;
  const searchInputRef = useRef(null);

  // Fetch states list for dropdown (used when zone is selected)
  const { data: statesData } = useQuery(
    ["general-info", "states"],
    () => getGeneralInfo("states"),
    { enabled: !!zone, staleTime: 10 * 60 * 1000 }
  );
  const statesForZone = React.useMemo(() => {
    if (!zone || !Array.isArray(statesData)) return [];
    return (statesData).filter(
      (item) => String(item?.zone || "").toLowerCase() === zone.toLowerCase()
    );
  }, [zone, statesData]);

  // Helper function to format date as YYYY-MM-DD (guards against Invalid Date)
  const formatDateTime = (date) => {
    if (!date || typeof date.getTime !== "function" || Number.isNaN(date.getTime())) {
      const fallback = new Date();
      date = fallback;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Helper function to format date as YYYY-MM-DD (for filenames)
  const formatDateOnly = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const normalizeSearchValue = (value) => {
    const v = String(value || "").trim();
    const hashIdMatch = v.match(/^#\s*(\d+)$/);
    if (hashIdMatch) return hashIdMatch[1];
    return v;
  };

  const handleSearchChange = useCallback((e) => {
    setSearchKey(e.target.value);
  }, []);

  const effectiveSearchValue = normalizeSearchValue(searchValue);

  // Export to CSV function
  const handleExportToCSV = async () => {
    setIsExporting(true);
    try {
      // Build dynamic query parameters based on current filters
      const params = new URLSearchParams({
        page: "1",
        limit: "1000", // Export more records
        sortField,
      startDate: formatDateTime(startDate),
      endDate: formatDateTime(endDate),
        exportToCsv: "true",
        ...(postingAccount && { postingAccount }),
        ...(zone && { zone }),
        ...(state && { state }),
        ...(status && { status }), // Include status in export params
      });

      const baseURL = process.env.REACT_APP_API_BASE_URL || 'https://digitalapi.palsonsderma.com';
      const response = await fetch(
        `${baseURL}/data/order?${params.toString()}`,
        {
          method: "GET",
          credentials: "include", // only if you're using cookies for auth
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      // Generate dynamic filename based on current filters
      const filename = `orders-${formatDateOnly(startDate)
        }-to-${formatDateOnly(endDate)}${postingAccount ? `-${postingAccount}` : ""
        }${zone ? `-${zone}` : ""}${state ? `-${state}` : ""}${searchValue ? `-${searchValue}` : ""}.csv`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url); // cleanup
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download CSV");
    } finally {
      setIsExporting(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Only search if there are at least 3 characters or if search is empty
      if (searchKey.length >= 3 || searchKey.length === 0) {
        // Only set searching to true if the search value is actually changing
        if (searchKey !== searchValue) {
          setIsSearching(true);
          setSearchValue(searchKey);
          setPage(1);
        } else {
          setIsSearching(false);
        }
      } else {
        // Reset searching state if search key is less than 3 characters
        setIsSearching(false);
      }
    }, 1000); // 1 second delay

    return () => clearTimeout(timeoutId);
  }, [searchKey, searchValue]);

  // Reset state filter when zone changes
  useEffect(() => {
    setState("");
  }, [zone]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [
    searchValue,
    postingAccount,
    zone,
    state,
    startDate,
    endDate,
    status,
    fetchType,
  ]);

  // Persist only dates with daily reset key
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          dateKey: TODAY_KEY,
          startDate,
          endDate,
        })
      );
    } catch (_) { }
  }, [startDate, endDate]);

  const { data: ordersData, isLoading } = useQuery(
    [
      "orders",
      fetchType, // Add fetch type to query key
      page,
      sortField,
      sortValue,
      effectiveSearchValue,
      postingAccount,
      zone,
      state,
      formatDateTime(startDate),
      formatDateTime(endDate),
      status, // Add status to query key
    ],
    () => {
      console.log("Orders query triggered with params:", {
        page,
        sortField,
        sortValue,
        searchValue: effectiveSearchValue,
        postingAccount,
        zone,
        state,
      startDate: formatDateTime(startDate),
      endDate: formatDateTime(endDate),
        status, // Log status in query params
      });
      // Set filtering state when query starts
      setIsFiltering(true);
      return getOrders({
        page,
        limit,
        sortField,
        sortValue,
        searchKey: effectiveSearchValue,
        postingAccount,
        zone,
        state,
      startDate: formatDateTime(startDate),
      endDate: formatDateTime(endDate),
        status, // Pass status to API
        fetchType, // Pass fetch type to API
      });
    },
    {
      keepPreviousData: false, // Don't keep previous data when fetch type changes
      refetchOnWindowFocus: false, // Prevent refetch on window focus
      refetchOnMount: true, // Refetch on component mount
      staleTime: 0, // Always consider data stale to ensure refetch on fetch type change
      onSuccess: () => {
        setIsSearching(false); // Reset searching state when query completes
        setIsFiltering(false); // Reset filtering state when query completes
      },
      onError: (error) => {
        console.error("Orders Error:", error);
        toast.error(error.message || "Failed to load orders");
        setIsSearching(false); // Reset searching state on error too
        setIsFiltering(false); // Reset filtering state on error too
      },
    }
  );

  // Helper function to get the correct data structure based on fetchType
  const getOrdersData = () => {
    if (fetchType === "order") {
      // Aggregated orders API returns nested data structure
      return ordersData?.data || [];
    } else {
      // Regular orders API returns direct data structure
      return ordersData?.data || [];
    }
  };

  // Helper function to get pagination info
  const getPaginationInfo = () => {
    return {
      count: ordersData?.count || 0,
      hasNext: ordersData?.hasNext || false,
    };
  };

  const orders = getOrdersData();
  const paginationInfo = getPaginationInfo();

  const handleSort = (field) => {
    if (field === sortField) {
      setSortValue(sortValue === "ASC" ? "DESC" : "ASC");
    } else {
      setSortField(field);
      setSortValue("ASC");
    }
  };


  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "delivered":
        return "badge-success text-white";
      case "processing":
        return "badge-warning text-gray-900";
      case "dispatched":
        return "badge-info text-white";
      case "out-for-delivery":
        return "badge-primary text-white";
      case "cancelled":
        return "badge-error text-white";
      case "return_processing":
        return "badge-warning text-gray-900";
      case "returned":
        return "badge-secondary text-white";
      case "pending":
        return "badge-info text-white";
      default:
        return "badge-info text-white";
    }
  };

  function formatDate(isoString) {
    const date = new Date(isoString);

    const options = {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };

    return date.toLocaleString("en-US", options);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Orders</h1>
          <button
            type="button"
            onClick={() => navigate("/orders/fix-entries")}
            className="btn btn-outline btn-sm"
          >
            Fix Order Entries
          </button>
        </div>
        <div className="text-sm text-gray-500">
          Total Orders: {paginationInfo.count || 0}
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 mb-4">
          <h3 className="text-base sm:text-lg font-semibold">Filters</h3>
          {isFiltering && (
            <div className="flex items-center gap-2 text-xs sm:text-sm text-info">
              <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-info"></div>
              <span>Applying filters...</span>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {/* Search Field */}
          <div className="form-control sm:col-span-2 lg:col-span-1">
            <label className="label py-1 sm:py-2">
              <span className="label-text text-xs sm:text-sm">Search</span>
            </label>
            <div className="relative">
              <input
                key="search-input"
                ref={searchInputRef}
                type="text"
                placeholder="Search by order number, customer, product... (min 3 characters)"
                className="text-xs sm:text-sm input input-bordered w-full pr-10"
                value={searchKey}
                onChange={handleSearchChange}
              />
              {isSearching && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-primary"></div>
                </div>
              )}
            </div>
            {searchKey.length > 0 && searchKey.length < 3 && (
              <label className="label py-0 sm:py-1">
                <span className="label-text-alt text-xs text-warning">
                  Please enter at least 3 characters to search
                </span>
              </label>
            )}
            {isSearching && searchKey.length >= 3 && (
              <label className="label py-0 sm:py-1">
                <span className="label-text-alt text-xs text-info">Searching...</span>
              </label>
            )}
          </div>

          {/* Posting Account Filter */}
          <div className="form-control">
            <label className="label py-1 sm:py-2">
              <span className="label-text text-xs sm:text-sm">Platform</span>
            </label>
            <div className="relative">
              <select
                className="text-xs sm:text-sm select select-bordered w-full pr-10"
                value={postingAccount}
                onChange={(e) => setPostingAccount(e.target.value)}
              >
                <option value="">All Platforms</option>
                <option value="amazon">Amazon</option>
                <option value="flipkart">Flipkart</option>
                <option value="shopify">Shopify</option>
                <option value="tata 1mg">Tata 1mg</option>
                <option value="palsons derma pvt ltd">Palsons Derma Pvt Ltd</option>
              </select>
              {isFiltering && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-primary"></div>
                </div>
              )}
            </div>
          </div>

          {/* Zone Filter */}
          <div className="form-control">
            <label className="label py-1 sm:py-2">
              <span className="label-text text-xs sm:text-sm">Zone</span>
            </label>
            <div className="relative">
              <select
                className="text-xs sm:text-sm select select-bordered w-full pr-10"
                value={zone}
                onChange={(e) => setZone(e.target.value)}
              >
                <option value="">All Zones</option>
                <option value="north">North</option>
                <option value="south">South</option>
                <option value="east">East</option>
                <option value="west">West</option>
                <option value="central">Central</option>
                <option value="north east">North East</option>
              </select>
              {isFiltering && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-primary"></div>
                </div>
              )}
            </div>
          </div>

          {/* State Filter (only when zone is selected) */}
          {zone && (
            <div className="form-control">
              <label className="label py-1 sm:py-2">
                <span className="label-text text-xs sm:text-sm">State</span>
              </label>
              <div className="relative">
                <select
                  className="text-xs sm:text-sm select select-bordered w-full pr-10"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                >
                  <option value="">All</option>
                  {statesForZone.map((item) => (
                    <option key={item.state} value={item.state}>
                      {item.state}
                    </option>
                  ))}
                </select>
                {isFiltering && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-primary"></div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Status Filter */}
          <div className="form-control">
            <label className="label py-1 sm:py-2">
              <span className="label-text text-xs sm:text-sm">Status</span>
            </label>
            <div className="relative">
              <select
                className="text-xs sm:text-sm select select-bordered w-full pr-10"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="dispatched">Dispatched</option>
                <option value="out-for-delivery">Out for Delivery</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
                <option value="return_processing">Return Processing</option>
                <option value="returned">Returned</option>
              </select>
              {isFiltering && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-primary"></div>
                </div>
              )}
            </div>
          </div>

          {/* Date Range */}
          <div className="form-control sm:col-span-2 lg:col-span-1">
            <label className="label py-1 sm:py-2">
              <span className="label-text text-xs sm:text-sm">Date Range</span>
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1 flex justify-center">
                <DatePicker
                  selected={startDate}
                  onChange={(date) => {
                    const d = new Date(date);
                    d.setHours(0, 0, 0, 0);
                    setStartDate(d);
                  }}
                  className="text-xs sm:text-sm input input-bordered w-full pr-6"
                  dateFormat="yyyy-MM-dd"
                  maxDate={endDate}
                />
                {isFiltering && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-primary"></div>
                  </div>
                )}
              </div>
              <div className="relative flex-1 flex justify-center">
                <DatePicker
                  selected={endDate}
                  onChange={(date) => {
                    const d = new Date(date);
                    d.setHours(23, 59, 59, 999);
                    setEndDate(d);
                  }}
                  className="text-xs sm:text-sm input input-bordered w-full pr-6"
                  dateFormat="yyyy-MM-dd"
                  minDate={startDate}
                  maxDate={new Date()}
                />
                {isFiltering && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-primary"></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Dropdown for fetch order item wise or order wise */}
          <div className="form-control">
            <label className="label py-1 sm:py-2">
              <span className="label-text text-xs sm:text-sm">Fetch Type</span>
            </label>
            <div className="relative">
              <select
                className="text-xs sm:text-sm select select-bordered w-full pr-10"
                value={fetchType}
                onChange={(e) => setFetchType(e.target.value)}
              >
                <option value="order">Order Wise</option>
                <option value="item">Item Wise</option>
              </select>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end mt-4 gap-2">
          <button
            className="btn btn-outline btn-xs sm:btn-sm flex items-center justify-center gap-1 sm:gap-2"
            onClick={() => setShowExcelUpload(true)}
          >
            <FiUpload size={14} className="sm:w-4 sm:h-4" />
            <span className="text-xs sm:text-sm">Upload Excel</span>
          </button>
          <button
            className="btn btn-primary btn-xs sm:btn-sm text-xs sm:text-sm"
            onClick={handleExportToCSV}
            disabled={isLoading || isFiltering || isExporting}
          >
            {isLoading || isFiltering || isExporting ? (
              <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white"></div>
            ) : (
              "Export CSV"
            )}
          </button>
          <button
            className="btn btn-outline btn-xs sm:btn-sm text-xs sm:text-sm"
            onClick={() => {
              setSearchKey("");
              setSearchValue("");
              setIsSearching(false);
              setIsFiltering(false);
              setPostingAccount("");
              setZone("");
              setStatus(""); // Clear status filter
              setFetchType("item"); // Reset fetch type to default
              setStartDate(
                new Date(new Date().setDate(new Date().getDate() - 30))
              );
              setEndDate(new Date());
              setPage(1);
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-8">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading orders...</span>
              </div>
            </div>
          ) : (
            <>
              {fetchType === "order" ? (
                <div>
                  <table className="table table-zebra w-full">
                    <thead>
                      <tr>
                        <th className="cursor-pointer hover:bg-gray-100">
                          Order ID
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100 w-full"
                          onClick={() => handleSort("refDate")}
                        >
                          Order Date{" "}
                          {sortField === "refDate" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100 w-full"
                          onClick={() => handleSort("entryDate")}
                        >
                          Entry Date{" "}
                          {sortField === "entryDate" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("customerName")}
                        >
                          Customer{" "}
                          {sortField === "customerName" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("platform")}
                        >
                          Platform{" "}
                          {sortField === "platform" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("totalItems")}
                        >
                          Total Items{" "}
                          {sortField === "totalItems" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("totalNetAmount")}
                        >
                          Total Amount{" "}
                          {sortField === "totalNetAmount" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100 w-[150px]"
                          onClick={() => handleSort("status")}
                        >
                          Status{" "}
                          {sortField === "status" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("zone")}
                        >
                          Zone{" "}
                          {sortField === "zone" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("state")}
                        >
                          State{" "}
                          {sortField === "state" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders?.map((order) => (
                        <tr key={order.refNo}>
                          <td className="whitespace-nowrap">
                            <span
                              className="cursor-pointer text-blue-500 hover:text-blue-600 transition-colors"
                              onClick={() =>
                                navigate(`/orders/detail/${order.refNo}`, {
                                  state: {
                                    fromPage: "/orders",
                                    startDate:
                                      formatDateTime(startDate),
                                    endDate:
                                      formatDateTime(endDate),
                                    postingAccount: postingAccount,
                                    zone: zone,
                                    state: state,
                                    status: status,
                                    fetchType: fetchType,
                                    searchValue: searchValue,
                                    sortField: sortField,
                                    sortValue: sortValue,
                                    page: page,
                                  },
                                })
                              }
                            >
                              {order.refNo}
                            </span>
                          </td>
                          <td className="whitespace-nowrap">
                            {formatDate(order.refDate)}
                          </td>
                          <td className="whitespace-nowrap">
                            {formatDate(order.entryDate)}
                          </td>
                          <td className="whitespace-nowrap">
                            {order.customerName || "N/A"}
                          </td>
                          <td>
                            <span className="badge badge-outline capitalize">
                              {order.platform}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className="badge badge-info text-white">
                              {order.totalItems}
                            </span>
                          </td>
                          <td className="font-medium text-green-600">
                            {formatAmountLegacy(order.totalNetAmount)}
                          </td>
                          <td>
                            <span
                              className={`badge ${getStatusBadgeClass(
                                order.status
                              )} whitespace-nowrap`}
                            >
                              {order.status}
                            </span>
                          </td>
                          <td>
                            <span className="badge badge-outline capitalize whitespace-nowrap">
                              {order.zone}
                            </span>
                          </td>
                          <td>
                            <span className="capitalize whitespace-nowrap">{order.state || "N/A"}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div>
                  <table className="table table-zebra w-full">
                    <thead>
                      <tr>
                        <th className="cursor-pointer hover:bg-gray-100">
                          Order ID
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100 w-full"
                          onClick={() => handleSort("refDate")}
                        >
                          Order Date{" "}
                          {sortField === "refDate" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("entryNo")}
                        >
                          Entry No{" "}
                          {sortField === "entryNo" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100 w-full"
                          onClick={() => handleSort("entryDate")}
                        >
                          Entry Date{" "}
                          {sortField === "entryDate" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("customerName")}
                        >
                          Customer{" "}
                          {sortField === "customerName" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("postingAccount")}
                        >
                          Platform{" "}
                          {sortField === "postingAccount" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("productName")}
                        >
                          Product{" "}
                          {sortField === "productName" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("sku")}
                        >
                          SKU{" "}
                          {sortField === "sku" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("qty")}
                        >
                          Qty{" "}
                          {sortField === "qty" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("amount")}
                        >
                          Amount{" "}
                          {sortField === "amount" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        {/* <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("commissionAmount")}
                        >
                          Commission Amount{" "}
                          {sortField === "commissionAmount" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("afterCommissionNetAmount")}
                        >
                          After Commission{" "}
                          {sortField === "afterCommissionNetAmount" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th> */}
                        <th
                          className="cursor-pointer hover:bg-gray-100 w-[150px]"
                          onClick={() => handleSort("status")}
                        >
                          Status{" "}
                          {sortField === "status" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("zone")}
                        >
                          Zone{" "}
                          {sortField === "zone" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                        <th
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort("state")}
                        >
                          State{" "}
                          {sortField === "state" &&
                            (sortValue === "ASC" ? "↑" : "↓")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders?.map((order) => (
                        <tr key={order.id}>
                          <td className="whitespace-nowrap">
                            {" "}
                            <span
                              className="cursor-pointer text-blue-500 hover:text-blue-600 transition-colors"
                              onClick={() =>
                                navigate(`/orders/detail/${order.refNo}`, {
                                  state: {
                                    fromPage: "/orders",
                                    startDate:
                                      formatDateTime(startDate),
                                    endDate:
                                      formatDateTime(endDate),
                                    postingAccount: postingAccount,
                                    zone: zone,
                                    state: state,
                                    status: status,
                                    fetchType: fetchType,
                                    searchValue: searchValue,
                                    sortField: sortField,
                                    sortValue: sortValue,
                                    page: page,
                                  },
                                })
                              }
                            >
                              #{order.id}
                            </span>
                            <br /> ({order.refNo})
                          </td>
                          <td className="whitespace-nowrap">
                            {formatDate(order.refDate)}
                            <br />
                            {order.refDate}
                          </td>
                          <td>
                            {fetchType === "order" ? "N/A" : order.entryNo}
                          </td>
                          <td className="whitespace-nowrap">
                            {formatDate(order.entryDate)}
                          </td>
                          <td className="whitespace-nowrap">
                            {order.customerName || "N/A"}
                          </td>
                          <td>
                            {fetchType === "order"
                              ? order.platform
                              : order.postingAccount}
                          </td>
                          <td>
                            {fetchType === "order" ? (
                              <span className="text-gray-500">
                                Multiple Items
                              </span>
                            ) : (
                              <span
                                className="whitespace-nowrap line-clamp-1 block cursor-pointer hover:text-blue-600 transition-colors"
                                style={{
                                  maxWidth: 180,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: "vertical",
                                  whiteSpace: "normal",
                                }}
                                title={order.productName}
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row click
                                  const skuForUrl = order.sku || order.channelSku || order.productName;
                                  navigate(
                                    `/analytics/product-detail/${encodeURIComponent(
                                      skuForUrl
                                    )}`,
                                    {
                                      state: {
                                        productName: order.productName,
                                        useSku: !!(order.sku || order.channelSku),
                                        fromPage: "/orders",
                                        startDate:
                                          startDate.toLocaleDateString("en-CA"),
                                        endDate:
                                          endDate.toLocaleDateString("en-CA"),
                                        postingAccount: postingAccount,
                                        zone: zone,
                                        state: state,
                                        status: status,
                                        fetchType: fetchType,
                                        searchValue: searchValue,
                                        sortField: sortField,
                                        sortValue: sortValue,
                                        page: page,
                                      },
                                    }
                                  );
                                }}
                              >
                                {order.productName}
                              </span>
                            )}
                          </td>
                          <td className="whitespace-nowrap">
                            {order.sku || "-"}
                          </td>
                          <td>
                            {fetchType === "order"
                              ? order.totalItems
                              : order.qty}
                          </td>
                          <td>
                            {formatAmountLegacy(
                              fetchType === "order"
                                ? order.totalNetAmount
                                : order.amount
                            )}
                          </td>
                          {/* <td className="font-medium text-orange-600">
                            {order.afterCommissionNetAmount ? formatAmountLegacy(order.amount - order.afterCommissionNetAmount) : "N/A"}
                          </td>
                          <td className="font-medium text-blue-600">
                            {order.afterCommissionNetAmount ? formatAmountLegacy(order.afterCommissionNetAmount) : "N/A"}
                          </td> */}
                          <td>
                            <span
                              className={`badge ${getStatusBadgeClass(
                                order.status
                              )} whitespace-nowrap`}
                            >
                              {order.status}
                            </span>
                          </td>
                          <td className="whitespace-nowrap">{order.zone}</td>
                          <td>
                            <span className="capitalize whitespace-nowrap">
                              {order.state || "N/A"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {(paginationInfo?.count ?? 0) > 0 && (
          <Pagination
            page={page}
            onPageChange={setPage}
            limit={limit}
            totalCount={paginationInfo.count}
            hasNext={paginationInfo.hasNext}
          />
        )}
      </div>

      {/* Excel Upload Modal */}
      {showExcelUpload && (
        <ExcelUpload
          onClose={() => setShowExcelUpload(false)}
          onSuccess={() => {
            // Optionally refresh the orders list or show success message
            toast.success("Orders uploaded successfully!");
          }}
        />
      )}
    </div>
  );
};

export default Orders;
